import type { TFunction } from 'i18next';

import type { Targeting } from '@feathr/blackbox';
import { TargetableClass } from '@feathr/blackbox';
import type { useStore } from '@feathr/extender/state';

import { determineInterestsMatchMode } from '../Interests';

/**
 * Represents a geo location item expanded from geo targetings.
 */
export interface IGeoLocationItem {
  /** The ID of the geo filter */
  geoFilterId: string;
  /** Display name of the geo location */
  name: string;
  /** Whether this location is included (true) or excluded (false) */
  included: boolean;
  /** The parent targeting this item belongs to */
  targeting: Targeting;
}

/**
 * Gets a description for the interests targeting match mode.
 *
 * @param targetings - Array of interest targetings
 * @param t - Translation function
 * @returns Description string for the match mode, or empty string if no targetings
 *
 * @example
 * const description = getInterestDescription(interestTargetings, t);
 * // Returns "any of the selected" or "all of the selected"
 */
export function getInterestDescription(targetings: Targeting[], t: TFunction): string {
  if (targetings.length === 0) {
    return '';
  }

  const matchMode = determineInterestsMatchMode(targetings);
  return matchMode === 'match_any' ? t('any of the selected') : t('all of the selected');
}

/**
 * Expands geo targetings into individual geo location items.
 *
 * Handles both geo_set targetables (which contain multiple included/excluded filters)
 * and individual geo targetables.
 *
 * @param geoTargetings - Array of geo targetings to expand
 * @param Targetables - Targetables store from useStore
 * @param GeoFilters - GeoFilters store from useStore
 * @returns Array of expanded geo location items
 *
 * @example
 * const items = expandGeoTargetings(geoTargetings, Targetables, GeoFilters);
 */
export function expandGeoTargetings(
  geoTargetings: Targeting[],
  Targetables: ReturnType<typeof useStore>['Targetables'],
  GeoFilters: ReturnType<typeof useStore>['GeoFilters'],
): IGeoLocationItem[] {
  const items: IGeoLocationItem[] = [];

  for (const targeting of geoTargetings) {
    const targetableId = targeting.get('target_data');
    if (!targetableId) {
      continue;
    }

    const targetable = Targetables.get(targetableId);
    if (!targetable) {
      continue;
    }

    const targetableClass = targetable.get('_cls');

    if (targetableClass === TargetableClass.geo_set) {
      const includedGeoFilters = targetable.get('included_geo_filters') ?? [];
      const excludedGeoFilters = targetable.get('excluded_geo_filters') ?? [];

      for (const geoFilterId of includedGeoFilters) {
        const geoFilter = GeoFilters.get(geoFilterId);
        if (geoFilter) {
          items.push({
            geoFilterId,
            name: geoFilter.get('name'),
            included: true,
            targeting,
          });
        }
      }

      for (const geoFilterId of excludedGeoFilters) {
        const geoFilter = GeoFilters.get(geoFilterId);
        if (geoFilter) {
          items.push({
            geoFilterId,
            name: geoFilter.get('name'),
            included: false,
            targeting,
          });
        }
      }
    } else if (targetableClass === TargetableClass.geo) {
      const geoFilterId = targetable.get('geo_filter');
      if (geoFilterId) {
        const geoFilter = GeoFilters.get(geoFilterId);
        if (geoFilter) {
          items.push({
            geoFilterId,
            name: geoFilter.get('name'),
            included: targeting.get('included') === true,
            targeting,
          });
        }
      }
    }
  }

  return items;
}

/**
 * Formats a targeting name for display, handling age demographic special case.
 *
 * @param targeting - The targeting to get the name for
 * @param t - Translation function
 * @returns Formatted name string
 *
 * @example
 * const name = formatTargetingName(targeting, t);
 * // Returns "25-34 years" for age demographic or the raw name otherwise
 */
export function formatTargetingName(targeting: Targeting, t: TFunction): string {
  return targeting.get('kind') === 'age_demographic'
    ? t('{{ range }} years', { range: targeting.get('name') })
    : targeting.get('name');
}
